import { useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/app/AuthProvider';
import type { Json, Tables } from '@/types/database';
import { normalizeAvatar, type AvatarConfig, type Facing } from './avatars';
import { SPAWN, defaultLayout, sanitizePosition, setActiveLayout, type OfficeLayout } from './map';
import { parseLayout } from './layout';

/**
 * Digital Office — persisted data layer (`digital_office_avatars`).
 *
 * Live state goes over Realtime (see presence.ts). These hooks only touch
 * the slow-moving snapshot: the look the user picked and where they were
 * last standing.
 */

export type OfficeAvatarRow = Tables<'digital_office_avatars'>;

const MY_KEY = (userId: string | undefined) => ['digital-office', 'me', userId];
const ALL_KEY = ['digital-office', 'avatars'];

export interface OfficeAvatarRecord {
  userId: string;
  config: AvatarConfig;
  position: { x: number; y: number; facing: Facing };
  lastSeenAt: string | null;
}

function toRecord(row: OfficeAvatarRow): OfficeAvatarRecord {
  const pos = sanitizePosition(row.last_x, row.last_y);
  const facing = (['up', 'down', 'left', 'right'] as Facing[]).includes(row.last_facing as Facing)
    ? (row.last_facing as Facing)
    : 'down';
  return {
    userId: row.user_id,
    config: normalizeAvatar(row),
    position: { x: pos.x, y: pos.y, facing },
    lastSeenAt: row.last_seen_at,
  };
}

/** The caller's own character, or `null` when they haven't created one yet. */
export function useMyOfficeAvatar() {
  const { user } = useAuth();
  return useQuery({
    queryKey: MY_KEY(user?.id),
    enabled: !!user?.id,
    queryFn: async (): Promise<OfficeAvatarRecord | null> => {
      const { data, error } = await supabase
        .from('digital_office_avatars')
        .select('*')
        .eq('user_id', user!.id)
        .maybeSingle();
      if (error) throw error;
      return data ? toRecord(data as OfficeAvatarRow) : null;
    },
  });
}

/**
 * Every character in the org — used for offline silhouettes + roster looks.
 *
 * `pollMs` turns on a refetch interval; the page uses it as a fallback
 * while the Realtime channel is down so persisted positions keep flowing.
 */
export function useOfficeAvatars(options: { pollMs?: number | false } = {}) {
  return useQuery({
    queryKey: ALL_KEY,
    queryFn: async (): Promise<OfficeAvatarRecord[]> => {
      const { data, error } = await supabase.from('digital_office_avatars').select('*');
      if (error) throw error;
      return (data as OfficeAvatarRow[]).map(toRecord);
    },
    staleTime: 30_000,
    refetchInterval: options.pollMs || false,
  });
}

/** Create or update the caller's character look. */
export function useSaveOfficeAvatar() {
  const qc = useQueryClient();
  const { user, profile } = useAuth();
  return useMutation({
    mutationFn: async (input: {
      config: AvatarConfig;
      position?: { x: number; y: number; facing: Facing };
    }): Promise<OfficeAvatarRecord> => {
      if (!user?.id || !profile?.organization_id) throw new Error('Not signed in.');
      const pos = input.position ?? { ...SPAWN, facing: 'down' as Facing };
      const { data, error } = await supabase
        .from('digital_office_avatars')
        .upsert(
          {
            user_id: user.id,
            organization_id: profile.organization_id,
            ...input.config,
            last_x: pos.x,
            last_y: pos.y,
            last_facing: pos.facing,
            last_seen_at: new Date().toISOString(),
          },
          { onConflict: 'user_id' },
        )
        .select('*')
        .single();
      if (error) throw error;
      return toRecord(data as OfficeAvatarRow);
    },
    onSuccess: (rec) => {
      qc.setQueryData(MY_KEY(user?.id), rec);
      qc.invalidateQueries({ queryKey: ALL_KEY });
    },
  });
}

/**
 * Fire-and-forget position checkpoint. Called on a slow cadence by the page
 * (every few seconds while walking, and when leaving) — never per frame.
 */
export async function persistOfficePosition(
  userId: string,
  pos: { x: number; y: number; facing: Facing },
): Promise<void> {
  const { error } = await supabase
    .from('digital_office_avatars')
    .update({
      last_x: pos.x,
      last_y: pos.y,
      last_facing: pos.facing,
      last_seen_at: new Date().toISOString(),
    })
    .eq('user_id', userId);
  if (error) console.warn('[digital-office] position checkpoint failed', error);
}

export const OFFICE_AVATARS_QUERY_KEY = ALL_KEY;

// ---------------------------------------------------------------------------
// Building layout (walls, desks, rooms, pallets) — one JSON doc per org
// ---------------------------------------------------------------------------

const LAYOUT_KEY = ['digital-office', 'layout'] as const;

export interface OfficeLayoutRecord {
  layout: OfficeLayout;
  updatedAt: string | null;
  isCustom: boolean;
}

export function useOfficeLayout() {
  const { profile } = useAuth();
  const orgId = profile?.organization_id;
  return useQuery({
    queryKey: [...LAYOUT_KEY, orgId],
    enabled: !!orgId,
    queryFn: async (): Promise<OfficeLayoutRecord> => {
      const { data, error } = await supabase
        .from('digital_office_layouts')
        .select('layout, updated_at')
        .eq('organization_id', orgId!)
        .maybeSingle();
      if (error) throw error;
      if (!data) {
        return { layout: defaultLayout(), updatedAt: null, isCustom: false };
      }
      return {
        layout: parseLayout(data.layout),
        updatedAt: data.updated_at,
        isCustom: true,
      };
    },
    staleTime: 15_000,
  });
}

export function useSaveOfficeLayout() {
  const qc = useQueryClient();
  const { user, profile } = useAuth();
  return useMutation({
    mutationFn: async (layout: OfficeLayout): Promise<OfficeLayoutRecord> => {
      if (!user?.id || !profile?.organization_id) throw new Error('Not signed in.');
      const { data, error } = await supabase
        .from('digital_office_layouts')
        .upsert(
          {
            organization_id: profile.organization_id,
            layout: layout as unknown as Json,
            updated_by: user.id,
          },
          { onConflict: 'organization_id' },
        )
        .select('layout, updated_at')
        .single();
      if (error) throw error;
      const rec: OfficeLayoutRecord = {
        layout: parseLayout(data.layout),
        updatedAt: data.updated_at,
        isCustom: true,
      };
      setActiveLayout(rec.layout);
      return rec;
    },
    onSuccess: (rec) => {
      qc.setQueryData([...LAYOUT_KEY, profile?.organization_id], rec);
    },
  });
}

/**
 * Keep other open maps (shop TVs, teammates) in sync when a super admin
 * saves a layout. No-ops when Realtime is unavailable — the next visit
 * still loads the saved document.
 */
export function useOfficeLayoutRealtime(enabled = true) {
  const qc = useQueryClient();
  const { profile } = useAuth();
  const orgId = profile?.organization_id;
  useEffect(() => {
    if (!enabled || !orgId) return;
    const channel = supabase
      .channel(`digital-office-layout:${orgId}`)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'digital_office_layouts', filter: `organization_id=eq.${orgId}` },
        (payload) => {
          const row = payload.new as { layout?: unknown; updated_at?: string } | null;
          if (row?.layout) {
            const rec: OfficeLayoutRecord = {
              layout: parseLayout(row.layout),
              updatedAt: row.updated_at ?? null,
              isCustom: true,
            };
            setActiveLayout(rec.layout);
            qc.setQueryData([...LAYOUT_KEY, orgId], rec);
          } else {
            void qc.invalidateQueries({ queryKey: [...LAYOUT_KEY, orgId] });
          }
        },
      )
      .subscribe();
    return () => {
      void supabase.removeChannel(channel);
    };
  }, [enabled, orgId, qc]);
}

export const OFFICE_LAYOUT_QUERY_KEY = LAYOUT_KEY;
